import { useEffect, useState } from "hono/jsx";

interface Room {
  id: string;
  players: number;
  state: string;
  hasPassword: boolean;
  ownerId?: string;
  isReserved?: boolean;
}

interface GameLobbyProps {
  title: string;
  description: string;
  wsUrl: string;
  createUrl: string;
  joinUrlPrefix: string;
  deleteUrlPrefix?: string; // Optional custom delete URL prefix if needed, default to createUrl logic
  user?: {
    id: string;
    reservedRooms?: any[];
    isAnonymous?: boolean;
    name?: string;
  };
  buttonText?: string;
  showReserved?: boolean;
}

export const GameLobby = ({
  title,
  description,
  wsUrl,
  createUrl,
  joinUrlPrefix,
  user,
  buttonText = "Start Game",
  showReserved = false,
}: GameLobbyProps) => {
  const [loading, setLoading] = useState(false);
  const [publicRooms, setPublicRooms] = useState<Room[]>([]);
  const [isPublic, setIsPublic] = useState(false);
  const [joinCode, setJoinCode] = useState("");
  const [creationError, setCreationError] = useState("");
  const [deletingRoomId, setDeletingRoomId] = useState<string | null>(null);

  // Auth & Reserved Rooms
  const [customCode, setCustomCode] = useState("");
  const [useReserved, setUseReserved] = useState(false);
  const [roomPasswords, setRoomPasswords] = useState<Record<string, string>>(
    {},
  );

  const [reservedRooms, setReservedRooms] = useState<any[]>([]);
  const hasReserved = user && user.reservedRooms &&
    user.reservedRooms.length > 0;

  useEffect(() => {
    if (hasReserved) {
      fetch("/api/rooms/reserved")
        .then((res) => res.json())
        .then((data) => {
          if (Array.isArray(data)) setReservedRooms(data);
        })
        .catch((err) => console.error("Failed to load reserved rooms", err));
    }
  }, [hasReserved]);

  useEffect(() => {
    const protocol = globalThis.location.protocol === "https:" ? "wss:" : "ws:";
    const fullWsUrl = `${protocol}//${globalThis.location.host}${wsUrl}`;
    const socket = new WebSocket(fullWsUrl);

    socket.onopen = () => console.log(`Connected to Lobby WS: ${wsUrl}`);

    socket.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === "ROOM_LIST") {
          setPublicRooms(msg.rooms);
        }
      } catch (e) {
        console.error("Failed to parse lobby message", e);
      }
    };

    socket.onclose = () => console.log("Lobby WS disconnected");

    return () => socket.close();
  }, [wsUrl]);

  const createRoom = async () => {
    setLoading(true);
    setCreationError("");
    try {
      // Reserved Logic
      let password = undefined;
      if (useReserved && customCode) {
        const room = reservedRooms.find((r: any) => r.id === customCode);
        if (room?.password) password = room.password;
      }

      const body = {
        ...(customCode ? { customId: customCode } : {}),
        ...(password ? { password } : {}),
        isPublic,
      };

      const res = await fetch(createUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await res.json();
      if (res.ok && data.roomId) {
        if (password) {
          localStorage.setItem(`room_pw_${data.roomId}`, password);
        }
        globalThis.location.href = `${joinUrlPrefix}/${data.roomId}`;
      } else if (data.error) {
        setCreationError(data.error);
      }
    } catch (e) {
      console.error("Failed to create room", e);
      setCreationError("Failed to create room");
    } finally {
      setLoading(false);
    }
  };

  const deleteRoom = async (roomId: string) => {
    setLoading(true);
    try {
      // Construct delete URL. createUrl is like "/api/bb/rooms". We need "/api/bb/rooms/:id"
      const baseUrl = createUrl; // e.g. /api/bb/rooms
      const url = `${baseUrl}/${roomId}`;

      const res = await fetch(url, { method: "DELETE" });
      const data = await res.json();

      if (res.ok) {
        // Remove from list immediately or wait for WS?
        // WS will send update.
        setDeletingRoomId(null);
      } else {
        alert(data.error || "Failed to delete room");
      }
    } catch (e) {
      console.error("Delete failed", e);
      alert("Delete failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div class="bg-base-200 flex flex-col items-center p-4 pt-12 min-h-[50vh]">
      <div class="text-center space-y-8 max-w-2xl w-full">
        <h1 class="text-6xl font-black text-primary tracking-tighter uppercase">
          {title}
        </h1>
        <p class="text-xl opacity-60 font-bold">{description}</p>

        {/* Main Action Buttons */}
        <div class="flex flex-col gap-4 max-w-sm mx-auto w-full">
          <button
            type="button"
            class="btn btn-primary btn-xl font-black uppercase text-xl shadow-lg border-b-4 border-primary-focus active:border-b-0 active:translate-y-1 transition-all"
            onClick={() =>
              (document.getElementById("create_modal") as HTMLDialogElement)
                .showModal()}
          >
            {buttonText}
          </button>

          <div class="divider font-black opacity-20">OR</div>

          <div class="join w-full shadow-lg">
            <input
              class="input input-bordered join-item font-mono font-bold text-center uppercase grow"
              placeholder="ROOM-ID"
              value={joinCode}
              onInput={(e) =>
                setJoinCode((e.target as HTMLInputElement).value.toUpperCase())}
            />
            <button
              type="button"
              class="btn btn-neutral join-item font-black"
              onClick={() => {
                if (joinCode) {
                  globalThis.location.href =
                    `${joinUrlPrefix}/${joinCode.toUpperCase()}`;
                }
              }}
            >
              JOIN
            </button>
          </div>
        </div>

        {/* Create Room Modal */}
        <dialog id="create_modal" class="modal">
          <div class="modal-box border border-base-300">
            <h3 class="font-black text-2xl mb-4">Create New Room</h3>
            {user && !user.isAnonymous && (
              <p class="mb-4 opacity-70">Customizing your game room.</p>
            )}

            <div class="flex flex-col gap-4">
              {showReserved && hasReserved && (
                <div class="flex items-center justify-center gap-2">
                  <span
                    class={`text-[10px] font-black uppercase transition-opacity ${
                      !useReserved ? "opacity-100" : "opacity-30"
                    }`}
                  >
                    Custom
                  </span>
                  <input
                    type="checkbox"
                    class="toggle toggle-primary toggle-sm"
                    checked={useReserved}
                    onChange={(e) =>
                      setUseReserved((e.target as HTMLInputElement).checked)}
                  />
                  <span
                    class={`text-[10px] font-black uppercase transition-opacity ${
                      useReserved ? "opacity-100" : "opacity-30"
                    }`}
                  >
                    Reserved
                  </span>
                </div>
              )}

              {(!useReserved)
                ? (
                  <>
                    {user && !user.isAnonymous && (
                      <>
                        <input
                          type="text"
                          placeholder="RANDOM CODE"
                          class="input input-bordered input-lg font-mono text-center uppercase font-black w-full"
                          value={customCode}
                          onInput={(e) =>
                            setCustomCode(
                              (e.target as HTMLInputElement).value
                                .toUpperCase(),
                            )}
                          maxLength={20}
                        />

                        <div class="form-control">
                          <label
                            class={`cursor-pointer label justify-start gap-4`}
                          >
                            <input
                              type="checkbox"
                              class="checkbox checkbox-primary"
                              checked={isPublic}
                              onChange={(e) =>
                                setIsPublic(
                                  (e.target as HTMLInputElement).checked,
                                )}
                            />
                            <span class="label-text font-black uppercase">
                              Public Room
                            </span>
                          </label>
                        </div>
                      </>
                    )}
                  </>
                )
                : (
                  <select
                    class="select select-bordered select-lg font-mono text-center uppercase font-black w-full"
                    onChange={(e) => {
                      const roomId = (e.target as HTMLSelectElement).value;
                      setCustomCode(roomId);
                      const room = reservedRooms.find((r: any) =>
                        r.id === roomId
                      );
                      if (room?.password) {
                        localStorage.setItem(
                          `room_pw_${roomId}`,
                          room.password,
                        );
                      }
                    }}
                  >
                    <option value="">Select ID</option>
                    {reservedRooms.map((r: any) => (
                      <option key={r.id} value={r.id}>
                        {r.password ? "🔒 " : ""}
                        {r.id} {r.label ? `(${r.label})` : ""}
                      </option>
                    ))}
                  </select>
                )}

              {creationError && (
                <div class="text-error font-bold text-center">
                  {creationError}
                </div>
              )}

              <button
                type="button"
                class={`btn btn-primary btn-lg w-full font-black uppercase ${
                  loading ? "loading" : ""
                }`}
                onClick={createRoom}
                disabled={loading}
              >
                {loading ? "Creating..." : "Create Room"}
              </button>

              {user && !user.isAnonymous && showReserved && (
                <a
                  href="/profile"
                  class="btn btn-ghost btn-xs opacity-50 hover:opacity-100 font-black uppercase"
                >
                  Manage Reserved Rooms
                </a>
              )}
            </div>

            <div class="modal-action">
              <form method="dialog">
                <button type="submit" class="btn">Close</button>
              </form>
            </div>
          </div>
        </dialog>

        {/* Public Rooms List */}
        {publicRooms.length > 0 && (
          <div class="w-full max-w-xl space-y-4 pt-8">
            <h3 class="text-xs uppercase font-black opacity-30 tracking-widest text-center">
              Live Public Games
            </h3>
            <div class="grid gap-2">
              {publicRooms.map((r) => {
                const isMine = user && user.id === r.ownerId;
                const isReserved = r.isReserved;

                return (
                  <div
                    key={r.id}
                    class="flex items-center justify-between p-4 bg-base-100 rounded-xl shadow-md border border-base-300 transform hover:scale-[1.01] transition-transform"
                  >
                    <div class="flex flex-col items-start">
                      <div class="flex items-center gap-2">
                        <span class="font-black font-mono text-lg">{r.id}</span>
                        {r.hasPassword && (
                          <span class="badge badge-error badge-xs">🔒</span>
                        )}

                        {isMine && (
                          <span class="badge badge-primary badge-xs">
                            OWNER
                          </span>
                        )}
                        {isReserved && (
                          <span class="badge badge-secondary badge-xs">
                            RESERVED
                          </span>
                        )}
                      </div>
                      <span class="text-[10px] opacity-50 uppercase font-black">
                        {r.players} Players • {r.state}
                      </span>
                    </div>
                    <div class="flex items-center gap-2">
                      {r.hasPassword && (
                        <input
                          type="password"
                          placeholder="PASSWORD"
                          class="input input-bordered input-sm w-24 font-bold text-center"
                          value={roomPasswords[r.id] || ""}
                          onInput={(e) =>
                            setRoomPasswords((prev) => ({
                              ...prev,
                              [r.id]: (e.target as HTMLInputElement).value,
                            }))}
                        />
                      )}
                      <button
                        type="button"
                        class="btn btn-sm btn-outline btn-primary font-black uppercase text-xs"
                        onClick={() => {
                          if (r.hasPassword && roomPasswords[r.id]) {
                            localStorage.setItem(
                              `room_pw_${r.id}`,
                              roomPasswords[r.id],
                            );
                          }
                          globalThis.location.href = `${joinUrlPrefix}/${r.id}`;
                        }}
                      >
                        Join
                      </button>

                      {isMine && (
                        <button
                          class="btn btn-sm btn-error btn-outline font-black"
                          onClick={() => setDeletingRoomId(r.id)}
                        >
                          RELEASE
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deletingRoomId && (
          <dialog open class="modal modal-open">
            <div class="modal-box border border-error">
              <h3 class="font-bold text-lg text-error">RELEASE ROOM?</h3>
              <p class="py-4 font-bold">
                Are you sure you want to release room{" "}
                <span class="font-mono bg-base-300 px-1">
                  {deletingRoomId}
                </span>?
                <br />
                <br />
                <span class="text-error uppercase">Warning:</span>{" "}
                All data executing world blocks, changes, and history will be
                {" "}
                <span class="underline">permanently erased</span>. There is no
                undo.
              </p>
              <div class="modal-action">
                <button class="btn" onClick={() => setDeletingRoomId(null)}>
                  Cancel
                </button>
                <button
                  class="btn btn-error"
                  onClick={() =>
                    deleteRoom(deletingRoomId)}
                >
                  CONFIRM RELEASE
                </button>
              </div>
            </div>
          </dialog>
        )}
      </div>
    </div>
  );
};
